import { Check, AlertCircle, Clock, Loader2, ChevronRight, Wrench } from 'lucide-react';
import { PipelineStageCard, type PipelineStageData } from './PipelineStageCard';

interface PipelineTimelineProps {
  stages: PipelineStageData[];
  hasRepair: boolean;
  totalDurationMs: number;
  totalTokens: number;
  model: string;
  utilityModel?: string | null;
}

function formatDuration(ms: number): string {
  if (ms >= 60000) return `${(ms / 60000).toFixed(1)}m`;
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${ms}ms`;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function StatusIcon({ status }: { status: PipelineStageData['status'] }) {
  switch (status) {
    case 'success':
      return <Check size={12} className="text-success" />;
    case 'error':
      return <AlertCircle size={12} className="text-ember" />;
    case 'running':
      return <Loader2 size={12} className="text-rune animate-spin" />;
    case 'skipped':
      return <Clock size={12} className="text-mist/50" />;
    default:
      return <Clock size={12} className="text-mist/30" />;
  }
}

export function PipelineTimeline({
  stages,
  hasRepair,
  totalDurationMs,
  totalTokens,
  model,
  utilityModel,
}: PipelineTimelineProps) {
  return (
    <div className="bg-night border border-stone/30 rounded-xl p-6 space-y-4">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg text-parchment">Generation Pipeline</h2>
          {hasRepair && (
            <span className="flex items-center gap-1 text-xs text-amber-400 bg-amber-400/10 px-2 py-1 rounded">
              <Wrench size={12} />
              Plan Repaired
            </span>
          )}
        </div>
        <div className="text-xs text-mist mt-1 flex flex-wrap gap-3">
          <span>
            Model: <span className="text-parchment">{model}</span>
          </span>
          {utilityModel && (
            <span>
              Utility: <span className="text-parchment">{utilityModel}</span>
            </span>
          )}
          <span>
            Duration: <span className="text-parchment">{formatDuration(totalDurationMs)}</span>
          </span>
          <span>
            Tokens: <span className="text-parchment">{formatNumber(totalTokens)}</span>
          </span>
        </div>
      </div>

      {/* Compact timeline view */}
      <div className="flex items-center gap-1 overflow-x-auto py-2">
        {stages.map((stage, i) => (
          <div key={stage.id} className="flex items-center">
            <div
              className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs ${
                stage.status === 'error'
                  ? 'bg-ember/20 text-ember'
                  : stage.status === 'success'
                    ? 'bg-stone/20 text-parchment'
                    : 'bg-stone/10 text-mist'
              }`}
              title={stage.title}
            >
              <StatusIcon status={stage.status} />
              <span className="whitespace-nowrap max-w-20 truncate">{stage.title}</span>
            </div>
            {i < stages.length - 1 && <ChevronRight size={14} className="text-mist/30 mx-0.5" />}
          </div>
        ))}
      </div>

      {/* Detailed stage cards */}
      <div className="space-y-3">
        {stages.map((stage, index) => (
          <PipelineStageCard key={stage.id} stage={stage} step={index + 1} />
        ))}
      </div>
    </div>
  );
}
